import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, withLatestFrom, mergeMap, switchMap } from 'rxjs/operators';

import * as BcpActions from './bcp.actions';
import { BcpService } from 'src/app/services/bcp.service';
import * as fromBcp from './bcp.selectors';
import { Observable, of } from 'rxjs';
import { updateStatusReset } from '../status/status.actions';
import { StatusType } from 'src/app/core/enum/status.enum';

@Injectable()
export class BcpEffects {
  loadBcp$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BcpActions.loadBcps),
      mergeMap((action) => {
        return this.bcpService
          .loadActiveBcp({ ...action.options, sort: 'id,desc' })
          .pipe(
            map((response: any) => {
              return BcpActions.loadBcpsSuccess({
                bcps: response.body.map((bcp) => ({
                  ...bcp,
                  name: bcp.saOrgLocation
                    ? `${bcp.name} @ ${bcp.saOrgLocation.name}`
                    : bcp.name,
                })),
                count: response.headers.get('x-total-count'),
              });
            })
          );
      })
    );
  });

  loadBcpRecoveryPhaseAll$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BcpActions.loadBcpRecoveryPhaseAll),
      mergeMap((action) => {
        return this.bcpService
          .loadBcpRecoveryPhaseAll(action.id)
          .pipe(
            map((recoveryPhases) =>
              BcpActions.loadBcpRecoveryPhaseSuccess({ recoveryPhases })
            )
          );
      })
    );
  });
  loadScenarios$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BcpActions.loadBcpScenarios),
      mergeMap((action) => {
        return this.bcpService.getBcpScenario(action.bcpId).pipe(
          map((response: any) => {
            return BcpActions.loadBcpScenariosSuccess({
              scenarios: response.body,
            });
          })
        );
      })
    );
  });

  loadBcpRecoveryPhase$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BcpActions.loadBcpRecoveryPhase),
      mergeMap((action) => {
        return this.bcpService
          .loadRecoveryPhaseMapping(action.id)
          .pipe(
            map((recoveryPhases) =>
              BcpActions.loadBcpRecoveryPhaseSuccess({ recoveryPhases })
            )
          );
      })
    );
  });

  createBcpScenarios$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BcpActions.createBcpScenarios),
      mergeMap((action) => {
        return this.bcpService.createScenario(action.scenario).pipe(
          map((response: any) => {
            this.store.dispatch(
              BcpActions.loadBcpScenarios({ bcpId: action.bcpId })
            );
            return BcpActions.dummyAction();
          })
        );
      })
    );
  });

  updateBcpScenarios$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BcpActions.updateBcpScenarios),
      mergeMap((action) => {
        return this.bcpService
          .updateBcpScenario(action.id, action.scenario)
          .pipe(
            map((response: any) => {
              this.store.dispatch(
                BcpActions.loadBcpScenarios({ bcpId: action.bcpId })
              );
              return BcpActions.dummyAction();
            })
          );
      })
    );
  });

  deleteRecoveryPhase$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BcpActions.deleteRecoveryPhase),
      mergeMap((action) => {
        return this.bcpService.deleteRecoveryPhase(action.id).pipe(
          map((_: any) => {
            return BcpActions.deleteRecoveryPhaseSuccess(action);
          })
        );
      })
    );
  });

  loadAdInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BcpActions.loadAdInfo),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBcp.getAdInfo, { id: action.id })
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, adInfo]) => {
        if (!adInfo) {
          return this.bcpService.getAdditionalInformation(action.id).pipe(
            map((response) => {
              return BcpActions.loadAdInfoSuccess({
                id: action.id,
                adInfoes: response.body,
              });
            })
          );
        }
        return of(BcpActions.dummyAction());
      })
    );
  });

  addOrUpdateAdInfo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BcpActions.addOrUpdateAdInfo),
      switchMap((action) => {
        let serviceToCall: Observable<any>;
        const adInfo = action.adInfo;
        const isFileUpload = adInfo.isFileUpload;
        delete adInfo.isFileUpload;
        if (action.adInfo.id) {
          serviceToCall = this.bcpService.updateAdditionalInformation(
            action.adInfo.id,
            action.adInfo
          );
        } else {
          serviceToCall = this.bcpService.addAdditionalInformation(
            action.adInfo
          );
        }
        return serviceToCall.pipe(
          map((response) => {
            if (isFileUpload)
              updateStatusReset(StatusType.Uploaded, this.store);
            return BcpActions.addOrUpdateAdInfoSuccess({
              id: action.id,
              adInfo: response,
            });
          })
        );
      })
    );
  });

  loadStaffRequirements$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(BcpActions.loadStaffRequirements),
      mergeMap(
        (action) =>
          of(action).pipe(
            withLatestFrom(
              this.store.select(fromBcp.getStaffRequirements, action)
            )
          ),
        (action, data) => data
      ),
      switchMap(([action, staffRequirements]) => {
        if (staffRequirements.length) {
          return of(BcpActions.dummyAction());
        }

        return this.bcpService
          .loadBiaStaffRequirements(action.biaIds, action.locationId)
          .pipe(
            map((response: any[]) =>
              BcpActions.loadStaffRequirementsSuccess({
                records: response,
                payload: action,
              })
            )
          );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private bcpService: BcpService,
    private store: Store
  ) {}
}
